import React from 'react';
import type { Recommendation } from '../types';
import { formatDateShort } from '../utils/date';
import { formatCompanyName } from '../utils/format';

interface RecommendationCardProps {
  rec: Recommendation;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const hasTradingData = (rec: Recommendation) =>
  Boolean(rec.has_trade || rec.trade_recommendation_id);

export function RecommendationCard({ rec, isExpanded, onToggleExpand }: RecommendationCardProps) {
  const decision = rec.ai_decision ?? '';
  const decisionClass = hasTradingData(rec) && decision ? `trade-decision-${decision.toLowerCase()}` : '';

  const handleCardClick = () => {
    if (hasTradingData(rec)) onToggleExpand();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (hasTradingData(rec) && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggleExpand();
    }
  };

  return (
    <div
      className={`signal-card ${isExpanded ? 'expanded' : ''} ${hasTradingData(rec) ? 'has-trade' : ''} ${decisionClass}`}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role={hasTradingData(rec) ? 'button' : undefined}
      tabIndex={hasTradingData(rec) ? 0 : undefined}
      aria-expanded={hasTradingData(rec) ? isExpanded : undefined}
    >
      <div className="card-main">
        <div className="card-header">
          <div className="ticker-info">
            <h3>{rec.ticker}</h3>
            <p>{formatCompanyName(rec.company_name)}</p>
          </div>
          <div className={`signal-badge signal-${rec.recommendation.toLowerCase()}`}>
            {rec.recommendation}
          </div>
        </div>

        <div className="card-metrics">
          <div className="metric">
            <span className="metric-label">Confidence</span>
            <div className="metric-value">
              <div className="confidence-bar">
                <div className="confidence-fill" style={{ width: `${rec.confidence * 100}%` }}></div>
              </div>
              <span>{(rec.confidence * 100).toFixed(0)}%</span>
            </div>
          </div>
          <div className="metric">
            <span className="metric-label">Sentiment</span>
            <span className={`sentiment-badge sentiment-${rec.sentiment.toLowerCase()}`}>
              {rec.sentiment}
            </span>
          </div>
          {rec.stock_price_at_time && (
            <div className="metric">
              <span className="metric-label">Price at Signal</span>
              <span className="metric-value">${rec.stock_price_at_time.toFixed(2)}</span>
            </div>
          )}
        </div>

        {rec.key_points.length > 0 && (
          <div className="key-insights">
            <h4>Key Insights</h4>
            <ul>
              {rec.key_points.map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="card-footer">
          <div className="meta">
            <span className="meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formatDateShort(rec.filing_date)}
            </span>
            <span className="meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              {rec.form_type}
            </span>
          </div>
          {hasTradingData(rec) && (
            <span className="card-click-hint">Click to view trade</span>
          )}
        </div>
      </div>
    </div>
  );
}
